import React from "react";
import { FriendRequest, User } from "../api/types.ts";
import { Button } from "../components/widgets/Button.tsx";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlineDotsVertical,
} from "react-icons/hi";
import {
  useAcceptFriendRequest,
  useCancelFriendRequest,
  useDeclineFriendRequest,
  useDeleteFriend,
  useOutgoingFriendRequests,
} from "../api/hooks.ts";
import { useDialog } from "../components/DialogManager.tsx";
import { AnimatedLoading } from "../components/widgets/AnimatedLoading.tsx";
import { Banner } from "../components/widgets/Banner.tsx";
import { FaUser, FaUserClock, FaUserPlus } from "react-icons/fa";
import { ImCheckmark, ImCross } from "react-icons/im";
import { MdMarkEmailUnread } from "react-icons/md";
import { Dropdown } from "../components/widgets/Dropdown.tsx";
import { DropdownItem } from "../components/widgets/DropdownItem.tsx";
import { isNotNullish } from "../util/typeGuards.ts";

const SocialPage: React.FC<{
  profile: User | undefined;
  profileLoading: boolean;
  profileError: Error | null;
  friends: Partial<User>[] | undefined;
  friendsError: Error | null;
  friendsLoading: boolean;
  friendRequests: FriendRequest[] | undefined;
  friendRequestsError: Error | null;
  friendRequestsLoading: boolean;
}> = ({
  profile,
  profileLoading,
  profileError,
  friends,
  friendsError,
  friendsLoading,
  friendRequests,
  friendRequestsError,
  friendRequestsLoading,
}: {
  profile: User | undefined;
  profileLoading?: boolean;
  profileError: Error | null;
  friends: Partial<User>[] | undefined;
  friendsError: Error | null;
  friendsLoading: boolean;
  friendRequests: FriendRequest[] | undefined;
  friendRequestsError: Error | null;
  friendRequestsLoading: boolean;
}) => {
  const { setError } = useDialog();

  const {
    data: outgoing,
    error: outgoingError,
    isLoading: outgoingLoading,
  } = useOutgoingFriendRequests();

  const declineFriendRequestMutation = useDeclineFriendRequest();
  const acceptFriendRequestMutation = useAcceptFriendRequest();
  const cancelFriendRequestMutation = useCancelFriendRequest();
  const deleteFriendMutation = useDeleteFriend();

  const { openDialog } = useDialog();

  const handleDeleteFriendClick = (friendId?: string, friendName?: string) => {
    openDialog("confirm", {
      onConfirm: () => () => {
        if (isNotNullish(friendId)) {
          deleteFriendMutation.mutate(friendId, {
            onError: (e) => {
              setError(e);
            },
          });
        }
      },
      title: "Remove Friend",
      message: `Are you sure you want to remove ${friendName} as a friend?`,
      confirmText: "Remove",
      variant: "danger",
    });
  };

  const handleCancelFriendRequestClick = (
    requestId: string,
    recipientEmail: string,
  ) => {
    openDialog("confirm", {
      onConfirm: () => {
        if (isNotNullish(requestId)) {
          cancelFriendRequestMutation.mutate(requestId, {
            onError: (e) => {
              setError(e);
            },
          });
        }
      },
      title: "Cancel Friend Request",
      message: `Are you sure you want to cancel friend request to ${recipientEmail}?`,
      confirmText: "Continue",
      variant: "danger",
    });
  };

  return (
    <div className="space-y-2 mx-auto max-w-2xl px-4 py-8">
      <button
        className="flex flex-grow items-center gap-2 text-primary hover:text-primary-dark transition-colors"
        onClick={() => window.history.back()}
      >
        <HiOutlineArrowNarrowLeft />
        Go Back
      </button>
      <div className="container mx-auto max-w-2xl">
        <div className="bg-white rounded-lg shadow-md p-6">
          {profileLoading ? (
            <AnimatedLoading />
          ) : profileError ? (
            <Banner variant="error" title="Api Error" visible />
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between">
                <h2>Social</h2>
                <Button
                  variant="primary"
                  onClick={() =>
                    openDialog("send-friend-request", {
                      friends: friends,
                      requests: friendRequests,
                      outgoing: outgoing,
                    })
                  }
                  disabled={
                    friendsLoading ||
                    friendRequestsLoading ||
                    outgoingLoading ||
                    profileLoading
                  }
                  aria-label="Send friend request"
                >
                  <FaUserPlus />
                  <span className="sm:block hidden">Send Friend Request</span>
                </Button>
              </div>
              <p className=" text-gray-700">
                View your friends' fitness journeys and share workout routine
                details using seriv.io's social features.
              </p>
              <h4 className="border-b pb-2 text-primary">Friends</h4>
              {friendsLoading ? (
                <AnimatedLoading />
              ) : friendsError ? (
                <Banner variant="error" title="Api Error" visible />
              ) : friends && friends?.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {friends.map((friend) => (
                    <div
                      key={friend.email}
                      className="bg-gray-50 rounded-md p-3"
                    >
                      <div className="flex flex-row justify-between items-center my-2">
                        <div>
                          <div className="flex flex-row gap-2 items-center">
                            <FaUser
                              size={16}
                              className="inline-block text-gray-600"
                            />
                            <span>{friend.name}</span>
                          </div>
                          <span className="text-xs text-gray-600 italic">
                            {friend.email}
                          </span>
                        </div>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Dropdown
                            trigger={
                              <button
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                aria-label="Friend options"
                              >
                                <HiOutlineDotsVertical
                                  className="text-gray-600"
                                  size={20}
                                />
                              </button>
                            }
                            alignment="left"
                          >
                            <DropdownItem
                              onClick={() =>
                                handleDeleteFriendClick(friend.id, friend.name)
                              }
                              className="text-red-600 hover:bg-red-50"
                            >
                              Remove
                            </DropdownItem>
                          </Dropdown>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="italic text-gray-600 text-sm  mb-2">
                  You have no friends. Try sending a friend request.
                </p>
              )}
              <h4 className="border-b pb-2 text-primary">Friend Requests</h4>
              <div className="pb-4">
                <p className="text-sm font-medium text-gray-500 mb-2">
                  Incoming
                </p>
                {friendRequestsLoading ? (
                  <AnimatedLoading />
                ) : friendRequestsError ? (
                  <Banner variant="error" title="Api Error" visible />
                ) : friendRequests && friendRequests?.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {friendRequests.map((req) => (
                      <div
                        key={req.sender_email}
                        className="bg-gray-50 rounded-md
                          p-3 mb-2"
                      >
                        <div className="flex flex-row justify-between my-2">
                          <div className="flex flex-row gap-2 items-center">
                            <MdMarkEmailUnread
                              size={16}
                              className="inline-block text-gray-600"
                            />
                            <span className="text-sm text-gray-600">
                              {req.sender_email}
                            </span>
                          </div>

                          <div className="grid-cols-2 sm:flex sm:flex-row sm:gap-2 ">
                            <Button
                              className="w-min mb-2 sm:mb-0"
                              variant="submit"
                              onClick={() =>
                                acceptFriendRequestMutation.mutate(req.id)
                              }
                              aria-label={`Accept friend request from ${req.sender_email}`}
                            >
                              <ImCheckmark size={12} />
                              <span className="sm:block hidden text-sm">
                                Accept
                              </span>
                            </Button>
                            <Button
                              className="w-min"
                              variant="danger"
                              onClick={() =>
                                declineFriendRequestMutation.mutate(req.id)
                              }
                              aria-label={`Decline friend request from ${req.sender_email}`}
                            >
                              <ImCross size={12} />
                              <span className="sm:block hidden text-sm">
                                Decline
                              </span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="italic text-gray-600 text-sm mb-2">
                    You have no incoming friend requests.
                  </p>
                )}
                <p className="text-sm font-medium text-gray-500 mb-2">
                  Outgoing
                </p>
                {outgoingLoading ? (
                  <AnimatedLoading />
                ) : outgoingError ? (
                  <Banner variant="error" title="Api Error" visible />
                ) : outgoing && outgoing?.length > 0 ? (
                  <div>
                    {outgoing.map((req) => (
                      <div
                        key={req.recipient_email}
                        className="bg-gray-50 rounded-md
                          p-3 mb-2"
                      >
                        <div className="flex flex-row justify-between my-2">
                          <div className="flex flex-row gap-2 items-center">
                            <FaUserClock
                              size={16}
                              className="inline-block text-gray-600"
                            />
                            <span className="text-sm text-gray-600">
                              {req.recipient_email}
                            </span>
                          </div>

                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <Dropdown
                              trigger={
                                <button
                                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                  aria-label="Friend options"
                                >
                                  <HiOutlineDotsVertical
                                    className="text-gray-600"
                                    size={20}
                                  />
                                </button>
                              }
                              alignment="left"
                            >
                              <DropdownItem
                                onClick={() =>
                                  handleCancelFriendRequestClick(
                                    req.id,
                                    req.recipient_email,
                                  )
                                }
                                className="text-red-600 hover:bg-red-50"
                              >
                                Cancel
                              </DropdownItem>
                            </Dropdown>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="italic text-gray-600 text-sm mb-2">
                    You have no outgoing friend requests.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { SocialPage };
